import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    type RefObject,
} from "react";
import { usePickerRoot } from "./PickerContext";
import { syncPickerFooterScrollPadding } from "./usePickerFooterScrollPadding";

type PickerRowScrollGuard = (row: HTMLElement | null) => void;

const PickerRowScrollGuardContext = createContext<PickerRowScrollGuard | null>(
    null,
);

export const PickerRowScrollGuardProvider =
    PickerRowScrollGuardContext.Provider;

/** The enclosing `Picker.List`'s row guard, or `null` for a row rendered outside one. */
export const usePickerRowScrollGuardRef = () =>
    useContext(PickerRowScrollGuardContext);

const HIGHLIGHT_KEYS = new Set([
    "ArrowUp",
    "ArrowDown",
    "Home",
    "End",
    "PageUp",
    "PageDown",
]);

/** Keeps the rows around the selection in view too. */
const INITIAL_REVEAL_OPTIONS: ScrollIntoViewOptions = {
    block: "center",
    inline: "nearest",
};

const isTextField = (target: EventTarget | null) =>
    target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

/**
 * Arrows, Home / End, and a Select popup's typeahead characters move the
 * highlight. Autofill and password managers dispatch `keydown` without a
 * `key` despite the DOM type, so read it defensively.
 */
const isHighlightKey = (event: KeyboardEvent) =>
    HIGHLIGHT_KEYS.has(event.key) ||
    (event.key?.length === 1 &&
        event.key !== " " &&
        !isTextField(event.target));

/**
 * Keeps a list's scroll position while its rows change. Base UI scrolls the
 * last selected row into view whenever that row's index moves while the list
 * is open (a parent expands or collapses above it, the value is replaced, the
 * query clears), not only on open, and offers no option to turn that off.
 * Rows handed the returned ref only scroll themselves into view for the
 * initial reveal (from each popup open until the next pointer or key press),
 * centered, and after a key that moves the highlight, as Base UI asked. Each
 * scroll lands above the list's pinned footer as it measures at that moment.
 */
export const usePickerRowScrollGuard = (
    listRef: RefObject<HTMLElement | null>,
): PickerRowScrollGuard => {
    const { isPopupOpen } = usePickerRoot();
    const isRevealPendingRef = useRef(true);
    const isHighlightKeyRef = useRef(false);

    // Base UI keeps the list mounted between opens. The press that opens the
    // popup lands before this runs; Base UI reveals the selected row after it
    // (from the root's layout effect, a frame later).
    useLayoutEffect(() => {
        if (isPopupOpen) {
            isRevealPendingRef.current = true;
        }
    }, [isPopupOpen]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            isRevealPendingRef.current = false;
            isHighlightKeyRef.current = isHighlightKey(event);
        };
        const handlePointerDown = () => {
            isRevealPendingRef.current = false;
            isHighlightKeyRef.current = false;
        };
        document.addEventListener("keydown", handleKeyDown, true);
        document.addEventListener("pointerdown", handlePointerDown, true);
        return () => {
            document.removeEventListener("keydown", handleKeyDown, true);
            document.removeEventListener(
                "pointerdown",
                handlePointerDown,
                true,
            );
        };
    }, []);

    return useCallback(
        (row: HTMLElement | null) => {
            if (row === null) {
                return;
            }
            // Base UI calls the row's own `scrollIntoView`, so the gate has to
            // sit on the element.
            Object.defineProperty(row, "scrollIntoView", {
                configurable: true,
                value: (options?: boolean | ScrollIntoViewOptions) => {
                    const isRevealPending = isRevealPendingRef.current;
                    if (isRevealPending || isHighlightKeyRef.current) {
                        syncPickerFooterScrollPadding(listRef.current);
                        Element.prototype.scrollIntoView.call(
                            row,
                            isRevealPending ? INITIAL_REVEAL_OPTIONS : options,
                        );
                    }
                },
            });
        },
        [listRef],
    );
};
